import { queryOptions } from "@tanstack/react-query";
import { fetchWithCSRF, clearCSRFToken, setCSRFToken } from "./csrf";
import type { User, Creator, Client, Project, Course, Testimonial } from "@shared/schema";

export async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  return fetchWithCSRF<T>(url, options);
}

export { clearCSRFToken, setCSRFToken };

// API Response Types
export interface PublicUser {
  id: string;
  username: string;
  email: string;
  name: string;
  role: "superadmin" | "admin" | "creator" | "client" | "student";
  avatarUrl?: string | null;
  bio?: string | null;
}

export interface AuthLoginResponse {
  user: PublicUser;
  csrfToken?: string;
}

export interface PortfolioItem {
  id: string;
  brand: string;
  title?: string;
  description?: string;
  image?: string;
  videoUrl?: string;
  category?: string;
  type?: string;
  result?: string;
  roas?: number;
  ctr?: number;
  views?: number;
  slug?: string;
  featured?: boolean;
  createdAt?: string;
}

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  content?: string;
  image?: string;
  author?: string;
  category?: string;
  tags?: string[];
  published?: boolean;
  publishedAt?: string;
  createdAt?: string;
}

export interface Event {
  id: string;
  title: string;
  slug?: string;
  description?: string;
  date: string;
  location?: string;
  image?: string;
  price?: number;
  capacity?: number;
  published?: boolean;
}

export interface Podcast {
  id: string;
  title: string;
  description?: string;
  audioUrl?: string;
  videoUrl?: string;
  image?: string;
  duration?: string;
  episodeNumber?: number;
  published?: boolean;
}

// Auth API
export const authApi = {
  login: async (username: string, password: string) => {
    const response = await fetchApi<AuthLoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    // Update CSRF token from login response (new session = new token)
    if (response.csrfToken) {
      setCSRFToken(response.csrfToken);
    }
    return response;
  },
  logout: async () => {
    return fetchApi<{ success: boolean }>("/api/auth/logout", {
      method: "POST",
    });
  },
  me: async () => {
    return fetchApi<{ user: PublicUser }>("/api/auth/me");
  },
};

// Public API queries
export const creatorsQueryOptions = queryOptions({
  queryKey: ["creators"],
  queryFn: () => fetchApi<Creator[]>("/api/creators"),
});

export const creatorBySlugQueryOptions = (slug: string) => queryOptions({
  queryKey: ["creator", slug],
  queryFn: () => fetchApi<Creator>(`/api/creators/${slug}`),
  enabled: !!slug,
});

export const coursesQueryOptions = queryOptions({
  queryKey: ["courses"],
  queryFn: () => fetchApi<Course[]>("/api/courses"),
});

export const courseBySlugQueryOptions = (slug: string) => queryOptions({
  queryKey: ["course", slug],
  queryFn: () => fetchApi<Course>(`/api/courses/${slug}`),
  enabled: !!slug,
});

export const eventsQueryOptions = queryOptions({
  queryKey: ["events"],
  queryFn: () => fetchApi<Event[]>("/api/events"),
});

export const podcastsQueryOptions = queryOptions({
  queryKey: ["podcasts"],
  queryFn: () => fetchApi<Podcast[]>("/api/podcasts"),
});

export const testimonialsQueryOptions = queryOptions({
  queryKey: ["testimonials"],
  queryFn: () => fetchApi<Testimonial[]>("/api/testimonials"),
});

export const portfolioQueryOptions = queryOptions({
  queryKey: ["portfolio"],
  queryFn: () => fetchApi<PortfolioItem[]>("/api/portfolio"),
});

export const portfolioByIdQueryOptions = (idOrSlug: string) => queryOptions({
  queryKey: ["portfolio", idOrSlug],
  queryFn: () => fetchApi<PortfolioItem>(`/api/portfolio/${idOrSlug}`),
  enabled: !!idOrSlug,
});

export const portfolioBySlugQueryOptions = portfolioByIdQueryOptions;

export const blogPostsQueryOptions = queryOptions({
  queryKey: ["blog"],
  queryFn: () => fetchApi<BlogPost[]>("/api/blog"),
});

export const blogPostBySlugQueryOptions = (slug: string) => queryOptions({
  queryKey: ["blog", slug],
  queryFn: () => fetchApi<BlogPost>(`/api/blog/${slug}`),
  enabled: !!slug,
});

// Admin API queries
export const adminStatsQueryOptions = queryOptions({
  queryKey: ["admin", "stats"],
  queryFn: () => fetchApi<any>("/api/admin/stats"),
});

export const adminAnalyticsQueryOptions = queryOptions({
  queryKey: ["admin", "analytics"],
  queryFn: () => fetchApi<any>("/api/admin/analytics"),
});

export const adminClientsQueryOptions = queryOptions({
  queryKey: ["admin", "clients"],
  queryFn: () => fetchApi<any[]>("/api/admin/clients"),
});

export const adminBrandInquiriesQueryOptions = queryOptions({
  queryKey: ["admin", "brand-inquiries"],
  queryFn: () => fetchApi<any[]>("/api/admin/brand-inquiries"),
});

export const adminProjectsQueryOptions = queryOptions({
  queryKey: ["admin", "projects"],
  queryFn: () => fetchApi<any[]>("/api/admin/projects"),
});

export const adminContactsQueryOptions = queryOptions({
  queryKey: ["admin", "contacts"],
  queryFn: () => fetchApi<any[]>("/api/admin/contacts"),
});

export const adminInvoicesQueryOptions = queryOptions({
  queryKey: ["admin", "invoices"],
  queryFn: () => fetchApi<any[]>("/api/admin/invoices"),
});

export const adminExpensesQueryOptions = (year?: number, quarter?: number) => queryOptions({
  queryKey: ["admin", "expenses", year, quarter],
  queryFn: () => {
    let url = "/api/admin/expenses";
    if (year && quarter) {
      url += `?year=${year}&quarter=${quarter}`;
    }
    return fetchApi<any[]>(url);
  },
});

export const adminCreatorsQueryOptions = queryOptions({
  queryKey: ["admin", "creators"],
  queryFn: () => fetchApi<any[]>("/api/creators?visible=false"),
});

export const adminEventsQueryOptions = queryOptions({
  queryKey: ["admin", "events"],
  queryFn: () => fetchApi<any[]>("/api/events?all=true"),
});

export const adminCoursesQueryOptions = queryOptions({
  queryKey: ["admin", "courses"],
  queryFn: () => fetchApi<any[]>("/api/courses?all=true"),
});

export const adminPodcastsQueryOptions = queryOptions({
  queryKey: ["admin", "podcasts"],
  queryFn: () => fetchApi<any[]>("/api/podcasts?all=true"),
});

export const adminPortfolioQueryOptions = queryOptions({
  queryKey: ["admin", "portfolio"],
  queryFn: () => fetchApi<any[]>("/api/portfolio?all=true"),
});

export const adminTestimonialsQueryOptions = queryOptions({
  queryKey: ["admin", "testimonials"],
  queryFn: () => fetchApi<any[]>("/api/testimonials?all=true"),
});

export const adminBlogPostsQueryOptions = queryOptions({
  queryKey: ["admin", "blog"],
  queryFn: () => fetchApi<any[]>("/api/blog?all=true"),
});

export const adminCouponsQueryOptions = queryOptions({
  queryKey: ["admin", "coupons"],
  queryFn: () => fetchApi<any[]>("/api/admin/coupons"),
});

export const adminAffiliatesQueryOptions = queryOptions({
  queryKey: ["admin", "affiliates"],
  queryFn: () => fetchApi<any[]>("/api/admin/affiliates"),
});

export const adminWebhooksQueryOptions = queryOptions({
  queryKey: ["admin", "webhooks"],
  queryFn: () => fetchApi<any[]>("/api/admin/webhooks"),
});

export const adminSettingsQueryOptions = queryOptions({
  queryKey: ["admin", "settings"],
  queryFn: () => fetchApi<any>("/api/admin/settings"),
});

// Creator API queries
export const creatorProjectsQueryOptions = queryOptions({
  queryKey: ["creator", "projects"],
  queryFn: () => fetchApi<any[]>("/api/creator/projects"),
});

export const creatorProfileQueryOptions = queryOptions({
  queryKey: ["creator", "profile"],
  queryFn: () => fetchApi<any>("/api/creator/profile"),
});

export const creatorPaymentsQueryOptions = queryOptions({
  queryKey: ["creator", "payments"],
  queryFn: () => fetchApi<any[]>("/api/creator/payments"),
});

export const creatorPaymentsSummaryQueryOptions = queryOptions({
  queryKey: ["creator", "payments", "summary"],
  queryFn: () => fetchApi<{
    totalPaid: number;
    totalPending: number;
    thisMonthEarnings: number;
    paidCount: number;
    pendingCount: number;
  }>("/api/creator/payments/summary"),
});

// Messages API queries
export const messagesQueryOptions = queryOptions({
  queryKey: ["messages"],
  queryFn: () => fetchApi<any[]>("/api/messages"),
});

export const unreadMessagesCountQueryOptions = queryOptions({
  queryKey: ["messages", "unread-count"],
  queryFn: () => fetchApi<{ count: number }>("/api/messages/unread-count"),
});

export const conversationQueryOptions = (userId: string) => queryOptions({
  queryKey: ["messages", "conversation", userId],
  queryFn: () => fetchApi<any[]>(`/api/messages/conversation/${userId}`),
});

export const usersForMessagingQueryOptions = queryOptions({
  queryKey: ["admin", "users-for-messaging"],
  queryFn: () => fetchApi<any[]>("/api/admin/users-for-messaging"),
});

// Mutations
export const contactsApi = {
  submit: async (data: any) => {
    return fetchApi<any>("/api/contacts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};

export const adminApi = {
  createCreator: async (data: any) => fetchApi<any>("/api/admin/creators", { method: "POST", body: JSON.stringify(data) }),
  updateCreator: async (id: string, data: any) => fetchApi<any>(`/api/admin/creators/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteCreator: async (id: string) => fetch(`/api/admin/creators/${id}`, { method: "DELETE", credentials: "include" }),
  
  createClient: async (data: any) => fetchApi<any>("/api/admin/clients", { method: "POST", body: JSON.stringify(data) }),
  updateClient: async (id: string, data: any) => fetchApi<any>(`/api/admin/clients/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteClient: async (id: string) => fetch(`/api/admin/clients/${id}`, { method: "DELETE", credentials: "include" }),
  
  updateBrandInquiry: async (id: string, data: any) => fetchApi<any>(`/api/admin/brand-inquiries/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteBrandInquiry: async (id: string) => fetch(`/api/admin/brand-inquiries/${id}`, { method: "DELETE", credentials: "include" }),
  
  createProject: async (data: any) => fetchApi<any>("/api/admin/projects", { method: "POST", body: JSON.stringify(data) }),
  updateProject: async (id: string, data: any) => fetchApi<any>(`/api/admin/projects/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteProject: async (id: string) => fetch(`/api/admin/projects/${id}`, { method: "DELETE", credentials: "include" }),
  
  createCourse: async (data: any) => fetchApi<any>("/api/admin/courses", { method: "POST", body: JSON.stringify(data) }),
  updateCourse: async (id: string, data: any) => fetchApi<any>(`/api/admin/courses/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteCourse: async (id: string) => fetch(`/api/admin/courses/${id}`, { method: "DELETE", credentials: "include" }),
  
  createEvent: async (data: any) => fetchApi<any>("/api/admin/events", { method: "POST", body: JSON.stringify(data) }),
  updateEvent: async (id: string, data: any) => fetchApi<any>(`/api/admin/events/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteEvent: async (id: string) => fetch(`/api/admin/events/${id}`, { method: "DELETE", credentials: "include" }),
  
  createPodcast: async (data: any) => fetchApi<any>("/api/admin/podcasts", { method: "POST", body: JSON.stringify(data) }),
  updatePodcast: async (id: string, data: any) => fetchApi<any>(`/api/admin/podcasts/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deletePodcast: async (id: string) => fetch(`/api/admin/podcasts/${id}`, { method: "DELETE", credentials: "include" }),
  
  createTestimonial: async (data: any) => fetchApi<any>("/api/admin/testimonials", { method: "POST", body: JSON.stringify(data) }),
  updateTestimonial: async (id: string, data: any) => fetchApi<any>(`/api/admin/testimonials/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteTestimonial: async (id: string) => fetch(`/api/admin/testimonials/${id}`, { method: "DELETE", credentials: "include" }),
  
  createPortfolio: async (data: any) => fetchApi<any>("/api/admin/portfolio", { method: "POST", body: JSON.stringify(data) }),
  updatePortfolio: async (id: string, data: any) => fetchApi<any>(`/api/admin/portfolio/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deletePortfolio: async (id: string) => fetch(`/api/admin/portfolio/${id}`, { method: "DELETE", credentials: "include" }),
  
  markContactRead: async (id: string) => fetch(`/api/admin/contacts/${id}/read`, { method: "PATCH", credentials: "include" }),

  // Invoices
  getNextInvoiceNumber: async () => fetchApi<{ invoiceNumber: string }>("/api/admin/invoices/next-number"),
  createInvoice: async (data: any) => fetchApi<any>("/api/admin/invoices", { method: "POST", body: JSON.stringify(data) }),
  updateInvoice: async (id: string, data: any) => fetchApi<any>(`/api/admin/invoices/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteInvoice: async (id: string) => fetch(`/api/admin/invoices/${id}`, { method: "DELETE", credentials: "include" }),

  // Expenses
  createExpense: async (data: any) => fetchApi<any>("/api/admin/expenses", { method: "POST", body: JSON.stringify(data) }),
  updateExpense: async (id: string, data: any) => fetchApi<any>(`/api/admin/expenses/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteExpense: async (id: string) => fetch(`/api/admin/expenses/${id}`, { method: "DELETE", credentials: "include" }),

  // Blog
  createBlogPost: async (data: any) => fetchApi<any>("/api/admin/blog", { method: "POST", body: JSON.stringify(data) }),
  updateBlogPost: async (id: string, data: any) => fetchApi<any>(`/api/admin/blog/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteBlogPost: async (id: string) => fetch(`/api/admin/blog/${id}`, { method: "DELETE", credentials: "include" }),

  // Coupons
  createCoupon: async (data: any) => fetchApi<any>("/api/admin/coupons", { method: "POST", body: JSON.stringify(data) }),
  updateCoupon: async (id: string, data: any) => fetchApi<any>(`/api/admin/coupons/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteCoupon: async (id: string) => fetch(`/api/admin/coupons/${id}`, { method: "DELETE", credentials: "include" }),

  // Affiliates
  createAffiliate: async (data: any) => fetchApi<any>("/api/admin/affiliates", { method: "POST", body: JSON.stringify(data) }),
  updateAffiliate: async (id: string, data: any) => fetchApi<any>(`/api/admin/affiliates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteAffiliate: async (id: string) => fetch(`/api/admin/affiliates/${id}`, { method: "DELETE", credentials: "include" }),

  // Webhooks
  createWebhook: async (data: any) => fetchApi<any>("/api/admin/webhooks", { method: "POST", body: JSON.stringify(data) }),
  updateWebhook: async (id: string, data: any) => fetchApi<any>(`/api/admin/webhooks/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteWebhook: async (id: string) => fetch(`/api/admin/webhooks/${id}`, { method: "DELETE", credentials: "include" }),
  testWebhook: async (id: string) => fetchApi<any>(`/api/admin/webhooks/${id}/test`, { method: "POST" }),

  // Site Settings
  getSettings: async () => fetchApi<any>("/api/admin/settings"),
  updateSettings: async (data: any) => fetchApi<any>("/api/admin/settings", { method: "PATCH", body: JSON.stringify(data) }),

  // Mass Notifications
  sendMassNotification: async (data: any) => fetchApi<any>("/api/admin/notifications/mass", { method: "POST", body: JSON.stringify(data) }),
};

// Creator API
export const creatorApi = {
  updateProject: async (id: string, data: any) => fetchApi<any>(`/api/creator/projects/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  updateProfile: async (data: any) => fetchApi<any>("/api/creator/profile", { method: "PATCH", body: JSON.stringify(data) }),
  checkSlug: async (slug: string) => fetchApi<{ available: boolean }>(`/api/creator/check-slug/${encodeURIComponent(slug)}`),
};

// Messages API
export const messagesApi = {
  send: async (data: { receiverId: string; subject?: string; content: string; projectId?: string }) => 
    fetchApi<any>("/api/messages", { method: "POST", body: JSON.stringify(data) }),
  markAsRead: async (id: string) => 
    fetch(`/api/messages/${id}/read`, { method: "PATCH", credentials: "include" }),
};

// Notifications API
export const notificationsQueryOptions = queryOptions({
  queryKey: ["notifications"],
  queryFn: () => fetchApi<any[]>("/api/notifications"),
});

export const notificationsUnreadCountQueryOptions = queryOptions({
  queryKey: ["notifications", "unread-count"],
  queryFn: () => fetchApi<{ count: number }>("/api/notifications/unread-count"),
});

export const notificationsApi = {
  markAsRead: async (id: string) => 
    fetch(`/api/notifications/${id}/read`, { method: "PATCH", credentials: "include" }),
  markAllAsRead: async () => 
    fetchApi<{ success: boolean }>("/api/notifications/mark-all-read", { method: "POST" }),
};

// Course Enrollments API
export const courseEnrollmentsApi = {
  enroll: async (data: { courseId: string; email: string; name: string; phone?: string; amount?: string; paymentMethod?: string }) => 
    fetchApi<any>("/api/course-enrollments", { method: "POST", body: JSON.stringify(data) }),
  checkEnrollment: async (email: string, courseId: string) => 
    fetchApi<{ enrolled: boolean; enrollment: any }>(`/api/course-enrollments/check?email=${encodeURIComponent(email)}&courseId=${encodeURIComponent(courseId)}`),
};

export const adminCourseEnrollmentsQueryOptions = queryOptions({
  queryKey: ["admin", "course-enrollments"],
  queryFn: () => fetchApi<any[]>("/api/admin/course-enrollments"),
});

export const adminCourseEnrollmentsApi = {
  update: async (id: string, data: any) => 
    fetchApi<any>(`/api/admin/course-enrollments/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: async (id: string) => 
    fetch(`/api/admin/course-enrollments/${id}`, { method: "DELETE", credentials: "include" }),
};

// Course Modules API
export const courseModulesApi = {
  getByCourse: async (courseId: string) => 
    fetchApi<any[]>(`/api/courses/${courseId}/modules`),
};

export const adminCourseModulesApi = {
  create: async (courseId: string, data: { title: string; description?: string; orderIndex: number; duration?: string; isFree?: boolean }) => 
    fetchApi<any>(`/api/admin/courses/${courseId}/modules`, { method: "POST", body: JSON.stringify(data) }),
  update: async (id: string, data: any) => 
    fetchApi<any>(`/api/admin/course-modules/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: async (id: string) => 
    fetch(`/api/admin/course-modules/${id}`, { method: "DELETE", credentials: "include" }),
};

// Course Lessons API
export const courseLessonsApi = {
  getByModule: async (moduleId: string) => 
    fetchApi<any[]>(`/api/course-modules/${moduleId}/lessons`),
};

export const adminCourseLessonsApi = {
  create: async (moduleId: string, data: { title: string; description?: string; content?: string; videoUrl?: string; duration?: string; orderIndex: number; type?: string; resources?: string; isFree?: boolean }) => 
    fetchApi<any>(`/api/admin/course-modules/${moduleId}/lessons`, { method: "POST", body: JSON.stringify(data) }),
  update: async (id: string, data: any) => 
    fetchApi<any>(`/api/admin/course-lessons/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: async (id: string) => 
    fetch(`/api/admin/course-lessons/${id}`, { method: "DELETE", credentials: "include" }),
};

// Course Progress API
export const courseProgressApi = {
  getByEnrollment: async (enrollmentId: string) => 
    fetchApi<any[]>(`/api/course-enrollments/${enrollmentId}/progress`),
  markComplete: async (enrollmentId: string, lessonId: string) => 
    fetchApi<any>(`/api/course-enrollments/${enrollmentId}/lessons/${lessonId}/complete`, { method: "POST" }),
};

// Student API
export const studentApi = {
  getEnrollments: async (email: string) => 
    fetchApi<any[]>(`/api/student/enrollments?email=${encodeURIComponent(email)}`),
  getCourseContent: async (courseId: string, email: string) => 
    fetchApi<{
      course: any;
      enrollment: any;
      modules: any[];
      progress: { completedLessons: string[]; percent: number };
    }>(`/api/student/courses/${courseId}?email=${encodeURIComponent(email)}`),
  getLesson: async (lessonId: string, email: string) => 
    fetchApi<{
      lesson: any;
      module: any;
      prevLesson: any | null;
      nextLesson: any | null;
      enrollmentId: string | null;
    }>(`/api/student/lessons/${lessonId}?email=${encodeURIComponent(email)}`),
  markLessonComplete: async (lessonId: string, email: string) => 
    fetchApi<{
      success: boolean;
      completedLessons: number;
      totalLessons: number;
      progressPercent: number;
    }>(`/api/student/lessons/${lessonId}/complete`, { 
      method: "POST", 
      body: JSON.stringify({ email }) 
    }),
  getCertificates: async (email: string) => 
    fetchApi<any[]>(`/api/student/certificates?email=${encodeURIComponent(email)}`),
  issueCertificate: async (email: string, courseId: string) => 
    fetchApi<any>(`/api/student/certificates`, { 
      method: "POST", 
      body: JSON.stringify({ email, courseId }) 
    }),
};

// Certificate Verification API
export const certificateApi = {
  verify: async (certificateNumber: string) => 
    fetchApi<{
      valid: boolean;
      studentName?: string;
      courseName?: string;
      issueDate?: string;
      certificateNumber?: string;
      error?: string;
    }>(`/api/certificates/verify/${encodeURIComponent(certificateNumber)}`),
};

export const studentEnrollmentsQueryOptions = (email: string) => queryOptions({
  queryKey: ["student", "enrollments", email],
  queryFn: () => studentApi.getEnrollments(email),
  enabled: !!email,
});

export const studentCourseContentQueryOptions = (courseId: string, email: string) => queryOptions({
  queryKey: ["student", "course", courseId, email],
  queryFn: () => studentApi.getCourseContent(courseId, email),
  enabled: !!courseId && !!email,
});

export const studentLessonQueryOptions = (lessonId: string, email: string) => queryOptions({
  queryKey: ["student", "lesson", lessonId, email],
  queryFn: () => studentApi.getLesson(lessonId, email),
  enabled: !!lessonId && !!email,
});

// Creator Applications API
export const creatorApplicationsApi = {
  submit: async (data: {
    name: string;
    email: string;
    phone?: string;
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    portfolioUrl?: string;
    niches?: string[];
    experience?: string;
    equipment?: string;
    motivation?: string;
  }) => fetchApi<any>("/api/creator-applications", { method: "POST", body: JSON.stringify(data) }),
  checkStatus: async (email: string) => 
    fetchApi<{ hasApplied: boolean; status: string | null }>(`/api/creator-applications/check?email=${encodeURIComponent(email)}`),
};

export const adminCreatorApplicationsQueryOptions = queryOptions({
  queryKey: ["admin", "creator-applications"],
  queryFn: () => fetchApi<any[]>("/api/admin/creator-applications"),
});

export const adminCreatorApplicationsApi = {
  get: async (id: string) => fetchApi<any>(`/api/admin/creator-applications/${id}`),
  update: async (id: string, data: { status?: string; reviewNotes?: string }) => 
    fetchApi<any>(`/api/admin/creator-applications/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: async (id: string) => 
    fetch(`/api/admin/creator-applications/${id}`, { method: "DELETE", credentials: "include" }),
};

// Contract Templates API
export const adminContractTemplatesQueryOptions = queryOptions({
  queryKey: ["admin", "contract-templates"],
  queryFn: () => fetchApi<any[]>("/api/admin/contract-templates"),
});

export const adminContractTemplatesApi = {
  get: async (id: string) => fetchApi<any>(`/api/admin/contract-templates/${id}`),
  create: async (data: { name: string; description?: string; content: string; category?: string; isDefault?: boolean }) => 
    fetchApi<any>("/api/admin/contract-templates", { method: "POST", body: JSON.stringify(data) }),
  update: async (id: string, data: any) => 
    fetchApi<any>(`/api/admin/contract-templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: async (id: string) => 
    fetch(`/api/admin/contract-templates/${id}`, { method: "DELETE", credentials: "include" }),
};

// Contracts API
export const adminContractsQueryOptions = queryOptions({
  queryKey: ["admin", "contracts"],
  queryFn: () => fetchApi<any[]>("/api/admin/contracts"),
});

export const adminContractsApi = {
  preview: async (data: { templateId?: string; projectId?: string; clientId?: string; creatorId?: string }) => 
    fetchApi<{ content: string; template: any }>("/api/admin/contracts/preview", { method: "POST", body: JSON.stringify(data) }),
  create: async (data: { templateId?: string; projectId?: string; clientId?: string; creatorId?: string; expiresAt?: string }) => 
    fetchApi<any>("/api/admin/contracts", { method: "POST", body: JSON.stringify(data) }),
  update: async (id: string, data: any) => 
    fetchApi<any>(`/api/admin/contracts/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: async (id: string) => 
    fetch(`/api/admin/contracts/${id}`, { method: "DELETE", credentials: "include" }),
};

// User Profile API
export interface UserProfile {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  avatarUrl?: string | null;
  bio?: string | null;
  phone?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  youtubeUrl?: string | null;
  websiteUrl?: string | null;
  createdAt?: string;
}

export const userProfileQueryOptions = queryOptions({
  queryKey: ["user", "profile"],
  queryFn: () => fetchApi<UserProfile>("/api/user/profile"),
});

export const userProfileApi = {
  get: async () => fetchApi<UserProfile>("/api/user/profile"),
  update: async (data: Partial<Omit<UserProfile, "id" | "username" | "email" | "role" | "createdAt">>) =>
    fetchApi<UserProfile>("/api/user/profile", { method: "PATCH", body: JSON.stringify(data) }),
};
